import { IFlagsConfig } from '@/models';
import {
  CONSENT_CHECKBOXES,
  ConsentCheckboxId,
  StoredDisclosureFlags,
} from '../config/checkboxes';
import { IConsentVariant } from '../config/variants';
import { groupCheckboxes } from './bucket-grants';

export type StoredLegalConsentFlags = StoredDisclosureFlags &
  Pick<IFlagsConfig, 'eulaAgreed'>;

// A disclosure is one of the two legal statements the Rest-of-World screen asks
// the user to accept: sharing their data with affiliates, and sharing it with
// third parties. Accepting one is stored as its own flag, separately from the
// buckets, so a later visit can drop that checkbox instead of asking again.

export const wasDisclosureAlreadyAccepted = (
  checkboxId: ConsentCheckboxId,
  flags: StoredDisclosureFlags
): boolean => {
  const flagKey = CONSENT_CHECKBOXES[checkboxId].disclosureFlagKey;
  return flagKey !== undefined && flags[flagKey] === true;
};

export const wasTermsAlreadyAccepted = (
  checkboxId: ConsentCheckboxId,
  flags: Pick<IFlagsConfig, 'eulaAgreed'>
): boolean =>
  CONSENT_CHECKBOXES[checkboxId].isTerms === true && flags.eulaAgreed === true;

// The variant's terms checkbox, if it has one. row-cs-held and legacy-null have none.
export const findTermsCheckboxId = (
  variant: IConsentVariant
): ConsentCheckboxId | undefined =>
  variant.checkboxes.find(
    checkboxId => CONSENT_CHECKBOXES[checkboxId].isTerms === true
  );

// hide accepted disclosures and terms. alwaysShowTerms keeps the terms checkbox.
export const resolveVisibleCheckboxes = (
  variant: IConsentVariant,
  flags: StoredLegalConsentFlags
): ConsentCheckboxId[] =>
  variant.checkboxes.filter(checkboxId => {
    if (wasDisclosureAlreadyAccepted(checkboxId, flags)) {
      return false;
    }
    if (variant.alwaysShowTerms) {
      return true;
    }
    return !wasTermsAlreadyAccepted(checkboxId, flags);
  });

export const isConsentUiEmpty = (
  variant: IConsentVariant,
  flags: StoredLegalConsentFlags
): boolean => resolveVisibleCheckboxes(variant, flags).length === 0;

// True when at least one checkbox from this one's group is still on screen, so
// the user can still finish the group. A checkbox in no group always qualifies.
const groupStillHasVisibleCheckbox = (
  checkboxId: ConsentCheckboxId,
  all: ConsentCheckboxId[],
  visible: ConsentCheckboxId[]
): boolean => {
  const groupId = CONSENT_CHECKBOXES[checkboxId].allOrNothingGroup;
  if (groupId === undefined) {
    return true;
  }
  return groupCheckboxes(groupId, all).some(memberId =>
    visible.includes(memberId)
  );
};

// ticks plus already-accepted disclosures and terms. a fully settled group is
// ignored while another checkbox is still on screen. empty screen counts all.
export const checkboxesCountedAsAccepted = (
  variant: IConsentVariant,
  checked: ConsentCheckboxId[],
  flags: StoredLegalConsentFlags
): ConsentCheckboxId[] => {
  const visible = resolveVisibleCheckboxes(variant, flags);
  if (visible.length === 0) {
    return variant.checkboxes.filter(
      checkboxId =>
        wasDisclosureAlreadyAccepted(checkboxId, flags) ||
        wasTermsAlreadyAccepted(checkboxId, flags)
    );
  }
  return variant.checkboxes.filter(
    checkboxId =>
      checked.includes(checkboxId) ||
      wasTermsAlreadyAccepted(checkboxId, flags) ||
      (wasDisclosureAlreadyAccepted(checkboxId, flags) &&
        groupStillHasVisibleCheckbox(checkboxId, variant.checkboxes, visible))
  );
};
